import React, { FC, useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { NumberParam, StringParam, useQueryParam } from "use-query-params";
import {
  StartPage,
} from "../../../../components";
import { useUi } from "../../../../context/ui.context";
import { projectService } from "../../../../api";
import { icons } from "../../../../icons";
import { extractErrorMessage } from "../../../../utils/error-extractor";
import { formatDate } from "../../../../utils/date-formatter";

type Project = {
  id: number;
  project_id: string;
  project_domain_id: string;
  deleted: boolean;
  disabled: boolean;
  fleio_disabled_reason: string | null;
  extras: any;
  sync_version: number;
  is_domain: boolean;
  name: string;
  description: string;
  created_at: string;
  updated_at: string;
  service: number;
  services_count: number;
};

const ProjectsList: FC = () => {
  const [t] = useTranslation();
  const navigate = useNavigate();
  const { viewAlert } = useUi();

  const [pageLoading, setPageLoading] = useState<boolean>(true);
  const [projects, setProjects] = useState<Project[]>([]);
  const [paginationData, setPaginationData] = useState<any>(null);

  const [page] = useQueryParam("page", NumberParam);
  const [ordering] = useQueryParam("ordering", StringParam);
  const [pageSize] = useQueryParam("page_size", NumberParam);
  const [searchString] = useQueryParam("search", StringParam);

  const updateList = useCallback(() => {
    setPageLoading(true);
    const params: Record<string, any> = {
      page: page || 1,
      pageSize: pageSize || 20,
      search: searchString || "",
      ordering: ordering || '-created_at',
    };

    projectService
      .getProjectList(params)
      .then((res) => {
        const data = res.data;
        const objects = (data?.objects || []) as Project[];
        setProjects(objects);
        setPaginationData(data);
      })
      .catch((error) => {
        const message =
          error?.response?.data?.detail ||
          extractErrorMessage(error?.response?.data) ||
          t("FAILED_TO_LOAD_PROJECTS");
        viewAlert({
          severity: "error",
          message,
        });
      })
      .finally(() => {
        setPageLoading(false);
      });
  }, [page, pageSize, ordering, searchString, t, viewAlert]);

  useEffect(() => {
    updateList();
  }, [updateList]);

  const handleDelete = useCallback(
    (project: Project) => {
      const confirmDelete = window.confirm(
        t("CONFIRM_DELETE_PROJECT", { name: project.name, id: project.project_id })
      );
      if (!confirmDelete) {
        return;
      }

      const deleteAllResources = window.confirm(
        t("DELETE_ALL_PROJECT_RESOURCES_CONFIRM")
      );

      setPageLoading(true);
      projectService
        .deleteProject(project.id, deleteAllResources)
        .then(() => {
          viewAlert({
            severity: "information",
            message: t("PROJECT_DELETED"),
          });
          updateList();
        })
        .catch((error) => {
          const message =
            error?.response?.data?.detail ||
            extractErrorMessage(error?.response?.data) ||
            t("FAILED_TO_DELETE_PROJECT");
          viewAlert({
            severity: "error",
            message,
          });
        })
        .finally(() => {
          setPageLoading(false);
        });
    },
    [t, updateList, viewAlert]
  );

  const columns = useMemo(
    () => [
      {
        title: t("NAME"),
        key: "name",
        sorted: true,
      },
      {
        title: t("PROJECT_ID"),
        key: "project_id",
        sorted: true,
      },
      {
        title: t("UPDATED_AT"),
        key: "updated_at_display",
        sorted: true,
      },
      {
        title: t("DESCRIPTION"),
        key: "description",
      },
      {
        title: t("STATUS"),
        key: "status_display",
      },
      {
        title: "",
        key: "props",
      },
    ],
    [t]
  );

  const items = useMemo(
    () => [
      {
        title: t("EDIT"),
        id: "edit",
        icon: icons.Edit,
        divider: false,
        onClick: (project: Project) => {
          navigate(`/openstack/projects/${project.id}/edit`);
        },
      },
      {
        title: t("EDIT_QUOTAS"),
        id: "edit-quotas",
        icon: icons.Settings,
        divider: false,
        onClick: (project: Project) => {
          navigate(`/openstack/projects/${project.id}/quotas`);
        },
      },
      {
        title: t("DELETE"),
        id: "delete",
        icon: icons.DeleteIcon,
        divider: false,
        onClick: (project: Project) => {
          handleDelete(project);
        },
      },
    ],
    [t, navigate, handleDelete]
  );

  const tableRows = useMemo(
    () =>
      projects.map((project) => ({
        ...project,
        updated_at_display: formatDate(project.updated_at),
        created_at_display: formatDate(project.created_at),
        status_display: project.disabled ? t("DISABLED") : (project.deleted ? t("DELETED") : t("ENABLED")),
        navigate: `/openstack/projects/${project.id}`,
      })),
    [projects, t]
  );

  return (
    <StartPage
      title={t("OPENSTACK_PROJECTS")}
      noResourceText={t("NO_PROJECTS")}
      createResourceButton={t("CREATE_PROJECT")}
      createResourceLink="/openstack/projects/create"
      haveResource={projects.length > 0}
      pageLoading={pageLoading}
      tableData={{
        columns: columns,
        data: tableRows,
        hideCheckbox: true,
        props: items,
        paginationData: paginationData,
        updateData: updateList,
      }}
    />
  );
};

export default ProjectsList;
